import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import API_BASE_URL from '../config';

// Use our own server's proxied /api/ route for the worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `${API_BASE_URL}/api/pdf-worker`;

const PDFThumbnail = ({ presentationId, title }) => {
  const containerRef = useRef(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // 1. Logic for Viewport Visibility (Intersection Observer)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // 2. Logic for Rendering and Caching
  useEffect(() => {
    if (!isVisible) return;

    let isMounted = true;
    const cacheKey = `pdf_thumb_${presentationId}`;

    const fetchAndRender = async () => {
      try {
        // Check local cache first
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          if (isMounted) {
            setImgUrl(cached);
            setLoading(false);
          }
          return;
        }

        const url = `${API_BASE_URL}/api/presentation-preview/${presentationId}`;
        
        // Load the PDF document with legacy settings
        const loadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: false,
          disableRange: true, // Better for Safari
          disableAutoFetch: true
        });

        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        // Use a lower scale for mobile memory limits (iOS Safari fix)
        const scale = 1.0; 
        const viewport = page.getViewport({ scale });
        
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        // Ensure dimensions are integers
        canvas.height = Math.floor(viewport.height);
        canvas.width = Math.floor(viewport.width);

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;
        
        // Use PNG for maximum compatibility across all iOS versions
        const dataUrl = canvas.toDataURL('image/png');
        
        if (isMounted) {
          setImgUrl(dataUrl);
          setLoading(false);
          
          try {
            localStorage.setItem(cacheKey, dataUrl);
          } catch (e) {
            // Ignore storage errors
          }
        }
      } catch (err) {
        console.error(`Error rendering PDF ID ${presentationId}:`, err.message);
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchAndRender();

    return () => {
      isMounted = false;
    };
  }, [presentationId, isVisible]);

  return (
    <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-gray-100">
      {loading ? (
        <div className="absolute inset-0 animate-pulse bg-gray-300 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      ) : error ? (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-amber-600 flex flex-col items-center justify-center text-white p-4 shadow-inner">
           <div className="mb-2 bg-white/20 p-3 rounded-full backdrop-blur-sm">
             <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
             </svg>
           </div>
           <span className="font-bold text-sm tracking-wide uppercase">Presentation</span>
           <span className="text-[10px] opacity-80 mt-1">Ready for Download</span>
        </div>
      ) : (
        <img
          src={imgUrl}
          alt={title}
          className="w-full h-full object-cover transition-opacity duration-500 opacity-100"
          width="400"
          height="225"
        />
      )}
    </div>
  );
};

export default PDFThumbnail;
